import { cpf, cnpj } from "cpf-cnpj-validator";
import predefinedCreditorTypes from "@/common/data/creditor-types.data";

/**
 * Validacao da lista de credores no navegador, antes do envio.
 *
 * Espelha CreditorListService.validateSpreadsheet e CreditorClassResolverHelper
 * do backend (mesma leitura via xlsx, mesmos apelidos de coluna e mesmas
 * mensagens), para que o usuario veja os erros por linha sem enviar o arquivo.
 * O servidor continua validando no upload; ao mudar uma regra la, ajuste aqui.
 */

const FICTITIOUS_MARKS = ["sim", "s", "x", "1", "true", "yes", "y", "ficto", "fictício", "ficticio"];

function normalizeText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

function normalizeHeader(value) {
  return normalizeText(value).replace(/[^a-z0-9]/g, "");
}

function getValue(row, aliases) {
  const normalizedAliases = aliases.map(normalizeHeader);
  const key = Object.keys(row).find((item) => normalizedAliases.includes(normalizeHeader(item)));
  return key ? row[key] : null;
}

function normalizeAmount(value) {
  if (value === null || value === undefined || value === "") return null;

  let normalizedValue = String(value).trim().replace(/[R$\s]/g, "");

  if (normalizedValue.includes(",") && normalizedValue.includes(".")) {
    const lastComma = normalizedValue.lastIndexOf(",");
    const lastDot = normalizedValue.lastIndexOf(".");

    normalizedValue = lastDot > lastComma
      ? normalizedValue.replace(/,/g, "")
      : normalizedValue.replace(/\./g, "").replace(",", ".");
  } else if (normalizedValue.includes(",")) {
    normalizedValue = /^\d{1,3}(,\d{3})+$/.test(normalizedValue)
      ? normalizedValue.replace(/,/g, "")
      : normalizedValue.replace(",", ".");
  } else if (/^\d{1,3}(\.\d{3})+$/.test(normalizedValue)) {
    normalizedValue = normalizedValue.replace(/\./g, "");
  }

  const amount = Number(normalizedValue);
  return Number.isFinite(amount) ? amount : null;
}

function splitMultiValue(value) {
  if (value === null || value === undefined || value === "") return [];

  return String(value)
    .split(/[,;]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

// Remove numeracao inicial ("2. Privilegio Geral" -> "privilegio geral").
function stripNumbering(value) {
  return normalizeText(value).replace(/^\d+\s*[.\-)]\s*/, "").trim();
}

const ROMAN_CLASSES = { i: 1, ii: 2, iii: 3, iv: 4, v: 5 };

// O numeral romano precisa ser a palavra inteira: "Classe Inexistente" nao e a classe I.
function predefinedAliasId(type) {
  const normalizedType = normalizeText(type);
  const roman = normalizedType.match(/\bclasse\s+(iv|v|i{1,3})\b/);
  const romanId = roman ? ROMAN_CLASSES[roman[1]] : null;

  if (normalizedType.includes("extraconcursal") || romanId === 5) return 5;
  if (normalizedType.includes("me/epp") || normalizedType.includes("me epp") || romanId === 4) return 4;
  if (normalizedType.includes("quirograf") || romanId === 3) return 3;
  if (normalizedType.includes("garantia real") || romanId === 2) return 2;
  if (normalizedType.includes("trabalhista") || romanId === 1) return 1;

  return null;
}

// Mesmo ficto que o processamento gera para credor sem documento (FictitiousRegistrationHelper).
function buildFictitiousRegistration(name) {
  const normalized = String(name || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  let hash = 0;
  for (const char of normalized) hash = (hash * 31 + char.charCodeAt(0)) % 100000000;
  return `000${String(hash).padStart(8, "0")}`;
}

function resolvePredefined(type) {
  const normalizedType = normalizeText(type);
  const numericType = Number(String(type || "").replace(/\D/g, ""));

  if (numericType >= 1 && numericType <= 5 && String(type || "").trim().replace(/\D/g, "") === String(type || "").trim()) {
    return predefinedCreditorTypes.find((x) => x.id === numericType) || null;
  }

  const aliasId = predefinedAliasId(normalizedType);
  if (aliasId) return predefinedCreditorTypes.find((x) => x.id === aliasId);

  return null;
}

function resolveClass(type, classes) {
  const raw = String(type === undefined || type === null ? "" : type).trim();
  if (!raw) return null;

  const list = Array.isArray(classes) && classes.length ? classes : predefinedCreditorTypes;
  const normalized = normalizeText(raw);
  const stripped = stripNumbering(raw);

  if (/^\d+$/.test(raw)) {
    const byId = list.find((item) => Number(item.id) === Number(raw));
    if (byId) return byId;
  }

  const exact = list.find((item) => normalizeText(item.name) === normalized)
    || list.find((item) => stripNumbering(item.name) === stripped);
  if (exact) return exact;

  const predefined = resolvePredefined(raw);
  if (predefined) {
    const inCase = list.find((item) => Number(item.id) === predefined.id);
    return inCase || predefined;
  }

  return null;
}

function describeClasses(classes) {
  return (Array.isArray(classes) && classes.length ? classes : predefinedCreditorTypes)
    .filter((item) => item.is_active !== false)
    .map((item) => item.name)
    .join(", ");
}

/**
 * Le a primeira aba do arquivo (CSV, XLS ou XLSX) como o servidor le:
 * objetos por linha, com os valores formatados (raw: false).
 */
export async function readCreditorListRows(file) {
  const XLSX = await import("xlsx");
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];

  if (!sheetName) return [];

  return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { raw: false, defval: "" });
}

/**
 * Valida as linhas lidas. `classes` sao as classes do caso (inclusive ocultas),
 * no formato de creditor-class.util. Devolve { errors: [{ row, message }], total }.
 */
export function validateCreditorListRows(rows, classes = []) {
  const errors = [];
  const duplicateKeys = new Map();

  if (!rows.length) {
    return { errors: [{ row: 1, message: "O arquivo está vazio ou contém apenas o cabeçalho." }], total: 0 };
  }

  let total = 0;

  rows.forEach((row, index) => {
    const position = index + 2;

    if (Object.values(row).join("").trim() === "") return;
    total++;

    const name = getValue(row, ["Nome completo", "Nome", "Name"]);
    const registrationRaw = getValue(row, ["CPF/CNPJ", "Registro", "Registration", "CPF", "CNPJ"]);
    const typeRaw = getValue(row, ["Classe", "Tipo", "Type", "Class"]);
    const amountRaw = getValue(row, ["Valor", "Value", "Amount"]);
    const currencyRaw = getValue(row, ["Moeda", "Currency"]);
    const emailRaw = getValue(row, ["E-mail", "Email"]);
    const phoneRaw = getValue(row, ["Celular", "Telefone", "Phone", "Phone Number"]);
    const fictoRaw = getValue(row, ["CPF/CNPJ ficto", "CPF ficto", "CNPJ ficto", "Documento ficto", "Ficto", "Fictício", "Ficticio"]);
    const isFictitious = FICTITIOUS_MARKS
      .includes(String(fictoRaw === undefined || fictoRaw === null ? "" : fictoRaw).trim().toLowerCase());
    const registration = registrationRaw ? String(registrationRaw).replace(/\D/g, "") : null;
    const amount = normalizeAmount(amountRaw);
    const currency = currencyRaw || currencyRaw === 0 ? Number(currencyRaw) : 1;
    const emails = splitMultiValue(emailRaw);
    const phones = splitMultiValue(phoneRaw);
    const type = typeRaw ? String(typeRaw).trim() : null;
    const creditorType = resolveClass(type, classes);

    if (!name) errors.push({ row: position, message: "Campo NOME é obrigatório." });
    if (!registration && !isFictitious) errors.push({ row: position, message: 'Campo CPF/CNPJ é obrigatório (ou marque a coluna "CPF/CNPJ ficto").' });
    if (amount === null) errors.push({ row: position, message: "Campo VALOR é obrigatório e deve ser numérico." });
    if (!type) errors.push({ row: position, message: "Campo CLASSE é obrigatório." });

    let isValidRegistration = isFictitious;
    if (registration && !isFictitious) {
      if (registration.length === 11 && !cpf.isValid(registration)) {
        errors.push({ row: position, message: "CPF inválido!" });
      } else if (registration.length === 14 && !cnpj.isValid(registration)) {
        errors.push({ row: position, message: "CNPJ inválido!" });
      } else if (registration.length !== 11 && registration.length !== 14) {
        errors.push({ row: position, message: "O registro deve ser um CPF ou CNPJ válido (11 ou 14 números)!" });
      } else {
        isValidRegistration = true;
      }
    }

    const isValidCurrency = Number.isInteger(currency) && currency >= 1;
    if (currencyRaw && !isValidCurrency) errors.push({ row: position, message: "Moeda inválida! Informe um código numérico válido." });

    const invalidEmails = emails.filter((item) => !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(item));
    if (invalidEmails.length) errors.push({ row: position, message: `E-mail inválido: ${invalidEmails.join(", ")}` });

    phones.forEach((item) => {
      const phoneDigits = item.replace(/\D/g, "");
      if (phoneDigits.length < 8 || phoneDigits.length > 15) {
        errors.push({ row: position, message: `Telefone inválido: ${item}. Informe entre 8 e 15 dígitos.` });
      }
    });

    if (type && !creditorType) {
      errors.push({ row: position, message: `Classe inválida ("${type}"). Use o nome exato de uma classe cadastrada no caso: ${describeClasses(classes)}.` });
    }

    if (isValidRegistration && creditorType && isValidCurrency) {
      // Sem documento, o ficto vem do nome: so e duplicado o mesmo nome na mesma classe e moeda.
      const keyRegistration = registration || (isFictitious && name ? buildFictitiousRegistration(name) : null);
      if (!keyRegistration) return;
      const duplicateKey = `${keyRegistration}|${creditorType.id}|${currency}`;
      const duplicateLines = duplicateKeys.get(duplicateKey) || [];
      duplicateLines.push(position);
      duplicateKeys.set(duplicateKey, duplicateLines);
    }
  });

  duplicateKeys.forEach((lines) => {
    if (lines.length <= 1) return;
    errors.push({ row: lines[0], message: `Credor duplicado na planilha. Mesmo CPF/CNPJ, classe e moeda nas linhas ${lines.join(", ")}.` });
  });

  return { errors, total };
}
